import React, { useState, useEffect } from 'react';
import { apiService } from '../../service/Api';

const ProspectSelector = ({ selectedCompany, selectedProspect, onSelectProspect, onCreateProspect }) => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    name: '',
    domain: '',
    intelligence_json: {
      deal_stage: '',
      description: ''
    }
  });

  useEffect(() => {
    if (selectedCompany?.id) {
      loadProspects();
    } else {
      setProspects([]);
    }
  }, [selectedCompany]);

  const loadProspects = async () => {
    if (!selectedCompany?.id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getProspectsByCompanyId(selectedCompany.id);
      if (response.data.success) {
        setProspects(response.data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load prospects');
      console.error('Error loading prospects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProspect = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!selectedCompany?.id) {
      setError('Please select a company first');
      setLoading(false);
      return;
    }

    try {
      const prospectData = {
        name: createFormData.name,
        domain: createFormData.domain,
        company_id: selectedCompany.id,
        intelligence_json: createFormData.intelligence_json
      };

      const response = await apiService.createProspect(prospectData);
      if (response.data.success) {
        const newProspect = response.data.data;
        setProspects([newProspect, ...prospects]);
        setCreateFormData({ 
          name: '', 
          domain: '', 
          intelligence_json: { deal_stage: '', description: '' } 
        });
        setShowCreateForm(false);
        onSelectProspect && onSelectProspect(newProspect);
        onCreateProspect && onCreateProspect(newProspect);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create prospect');
      console.error('Error creating prospect:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Prospect</h3>
        <button
          type="button"
          onClick={() => setShowCreateForm(!showCreateForm)}
          disabled={!selectedCompany}
          className={`text-sm px-4 py-2 rounded-md transition-colors ${
            selectedCompany 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {showCreateForm ? 'Cancel' : '+ New Prospect'}
        </button>
      </div>

      {!selectedCompany && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-700 text-sm">
          Please select a company first
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {showCreateForm ? (
        <form onSubmit={handleCreateProspect} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prospect Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={createFormData.name}
              onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domain
            </label>
            <input
              type="text"
              value={createFormData.domain}
              onChange={(e) => setCreateFormData({ ...createFormData, domain: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deal Stage
            </label>
            <select
              value={createFormData.intelligence_json.deal_stage}
              onChange={(e) => setCreateFormData({
                ...createFormData,
                intelligence_json: {
                  ...createFormData.intelligence_json,
                  deal_stage: e.target.value
                }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select deal stage...</option>
              <option value="appointmentscheduled">Appointment Scheduled</option>
              <option value="qualifiedtobuy">Qualified to Buy</option>
              <option value="presentationscheduled">Presentation Scheduled</option>
              <option value="decisionmakerboughtin">Decision Maker Bought In</option>
              <option value="contractsent">Contract Sent</option>
              <option value="closedwon">Closed Won</option>
              <option value="closedlost">Closed Lost</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={createFormData.intelligence_json.description}
              onChange={(e) => setCreateFormData({
                ...createFormData,
                intelligence_json: {
                  ...createFormData.intelligence_json,
                  description: e.target.value
                }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="4"
              placeholder="Enter prospect description and context..."
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || !selectedCompany}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
            >
              {loading ? 'Creating...' : 'Create Prospect'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div>
          {!selectedCompany ? (
            <div className="text-center py-8 text-gray-500">Select a company to view prospects</div>
          ) : loading && prospects.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Loading prospects...</div>
          ) : prospects.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No prospects found for this company. Create one to get started.
            </div>
          ) : (
            <select
              value={selectedProspect?.id || ''}
              onChange={(e) => {
                const prospect = prospects.find(p => p.id === parseInt(e.target.value));
                onSelectProspect && onSelectProspect(prospect);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a prospect...</option>
              {prospects.map((prospect) => (
                <option key={prospect.id} value={prospect.id}>
                  {prospect.name || prospect.prospect_name}
                </option>
              ))}
            </select>
          )}

          {selectedProspect && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {selectedProspect.name || selectedProspect.prospect_name}
                  </p>
                  {selectedProspect.domain && (
                    <p className="text-sm text-gray-600 mt-1">{selectedProspect.domain}</p>
                  )}
                  {selectedProspect.intelligence_json?.deal_stage && (
                    <p className="text-sm text-blue-600 mt-2">
                      Stage: {selectedProspect.intelligence_json.deal_stage}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onSelectProspect && onSelectProspect(null)}
                  className="text-gray-400 hover:text-gray-600 ml-4"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProspectSelector;
